import {
  ApprovalStepTypeEnum,
  ApproverValidationInputModel,
  ApproverValidationViewModel,
  TenantViewModel,
} from '@ail/customers-api'
import {
  ApprovalProcessConfigInputModel,
  ApprovalProcessConfigViewModel,
  ApprovalProcessStepConfigViewModel,
  ApprovalProcessStepKind,
} from '@ail/orders-api'
import React, { Component } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { ConnectedProps, connect } from 'react-redux'
import { NavLink, RouteComponentProps } from 'react-router-dom'
import { Arr } from '../../../helpers/Arr'
import { ObjectHelper } from '../../../helpers/ObjectHelper'
import { PermissionManager } from '../../../helpers/PermissionManager'
import { wrapQuery } from '../../../redux/api/apiQuery'
import { customersApi } from '../../../redux/api/customerApi'
import { ordersApi } from '../../../redux/api/ordersApi'
import { State } from '../../../redux/reducers/initialState'
import { ICustomerViewModel, IUnitViewModel } from '../../../types/interfaces'
import Alert from '../../common/Alert/Alert'
import Confirm from '../../common/Confirm/Confirm'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import './NewWorkflow.scss'
import { Loader } from '../../common/Loader/Loader'

const newProcessId = 'new'
const stepTypes = Object.values(ApprovalProcessStepKind)

interface IRouteParams {
  id: string
}

interface IAlertState {
  visible: boolean
  message: string
  title: string
}

type IRequestAdminPanelProps = ConnectedProps<typeof connector> & WithTranslation & RouteComponentProps<IRouteParams>

interface OwnerOfID {
  type: string
  name: string
}

interface UnitsOfCompanyDisplay {
  id: string
  show: boolean
}

interface StepWithError extends ApproverValidationViewModel {
  errorInStep?: boolean
}

interface IEditWorkflowState {
  workflowsConfigs: ApprovalProcessConfigViewModel[]
  tenants: TenantViewModel[]
  jsonChanged: boolean
  approversValidating: boolean
  savingError: IAlertState
  currentWorkflow: ApprovalProcessConfigViewModel
  workflowForSteps?: ApprovalProcessConfigViewModel
  showUnits: UnitsOfCompanyDisplay[]
  approvalProcessToSend: string
  ownerIdToCheck: string
  ownerOfID: OwnerOfID
  currentApprovalProcess?: ApprovalProcessConfigViewModel
  loading: boolean
  validationState: StepWithError[]
  incompleteSteps: ApprovalProcessStepConfigViewModel[]
  isTagBased: boolean
  validCompanies: ICustomerViewModel[]
  validUnits: IUnitViewModel[]
  invalidCompanies: string[]
  confirmingSave: boolean
}

class RequestsAdminPanel extends Component<IRequestAdminPanelProps, IEditWorkflowState> {
  constructor(props: IRequestAdminPanelProps) {
    super(props)
    this.state = {
      workflowsConfigs: [],
      tenants: [],
      approversValidating: false,
      validUnits: [],
      validCompanies: [],
      jsonChanged: false,
      savingError: {
        message: 'nothing',
        title: 'nothing',
        visible: false,
      },
      ownerIdToCheck: '',
      approvalProcessToSend: '',
      currentWorkflow: {
        id: '',
        name: '',
        owner: '',
        totalSteps: 0,
        tr: {},
        validCompanies: [],
      },
      ownerOfID: {
        type: '',
        name: '',
      },
      loading: true,
      validationState: [],
      showUnits: [],
      incompleteSteps: [],
      isTagBased: false,
      invalidCompanies: [],
      confirmingSave: false,
    }
  }

  componentDidMount() {
    if (!this.state.workflowsConfigs.length) {
      this.props.getWorkflows().then((workflowsConfigs) => this.setState({ workflowsConfigs }))
    } else {
      this.defineWorkflow()
    }
    if (!this.state.tenants.length) {
      this.props.loadTenants().then((tenants) => this.setState({ tenants }))
    }
  }

  componentDidUpdate(oldProps: IRequestAdminPanelProps, oldState: IEditWorkflowState) {
    const { currentWorkflow, workflowForSteps } = this.state
    const { customers } = this.props
    if (this.state.workflowsConfigs !== oldState.workflowsConfigs || this.state.tenants !== oldState.tenants) {
      this.defineWorkflow()
    }

    if (
      this.props.match.params.id !== newProcessId &&
      currentWorkflow !== oldState.currentWorkflow &&
      !!currentWorkflow
    ) {
      this.setState({
        ownerIdToCheck: currentWorkflow.owner,
        approvalProcessToSend: JSON.stringify(currentWorkflow, null, '\t'),
      })
    }

    if (
      this.state.tenants !== oldState.tenants ||
      (this.state.ownerIdToCheck?.length > 35 && this.state.ownerIdToCheck !== oldState.ownerIdToCheck)
    ) {
      let ownerOfID: OwnerOfID = { type: '', name: '' }

      this.state.tenants.filter((el) => {
        if (el.id === this.state.ownerIdToCheck) {
          ownerOfID = { type: 'TENANT', name: el.name }
        }
      })
      if (!ownerOfID.name && !ownerOfID.type) {
        this.props.customers.filter((el) => {
          if (el.id === this.state.ownerIdToCheck) {
            ownerOfID = { type: 'CUSTOMER', name: el.name }
          } else {
            el.units.filter((un) => {
              if (un.id === this.state.ownerIdToCheck) {
                ownerOfID = { type: 'UNIT', name: el.name }
              }
            })
          }
        })
      }
      this.setState({
        ownerOfID,
      })
      if (!!ownerOfID.name && !!ownerOfID.type) {
        const updatedCurrentWorkflow = ObjectHelper.clone({
          ...this.state.currentWorkflow,
          owner: this.state.ownerIdToCheck,
        })
        this.setState({
          approvalProcessToSend: JSON.stringify(updatedCurrentWorkflow, null, '\t'),
        })
      }
    }

    if (workflowForSteps !== oldState.workflowForSteps && !!workflowForSteps) {
      const array: UnitsOfCompanyDisplay[] = []
      const validCompaniesList: ICustomerViewModel[] = []
      const semiValidCompaniesList: ICustomerViewModel[] = []
      const invalidCompaniesIds: string[] = []
      const units: IUnitViewModel[] = []
      workflowForSteps?.validCompanies?.map((el) => array.push({ id: el, show: false }))
      if (workflowForSteps?.validCompanies?.length) {
        workflowForSteps?.validCompanies?.forEach((el) => {
          customers?.filter((cust) => {
            if (cust.id === el) {
              if (cust?.tenantId?.toLowerCase() !== workflowForSteps?.owner?.toLowerCase()) {
                semiValidCompaniesList.unshift(cust)
              } else {
                validCompaniesList.push(cust)
              }
            }
          })
          customers?.forEach((cust) =>
            cust.units.filter((un) => {
              if (un.id === el) {
                units.push(un)
              }
            })
          )
          if (
            !invalidCompaniesIds.includes(el) &&
            validCompaniesList.filter((vc) => vc.id.indexOf(el) > -1).length === 0 &&
            semiValidCompaniesList.filter((vc) => vc.id.indexOf(el) > -1).length === 0
          ) {
            invalidCompaniesIds.push(el)
          }
        })
        validCompaniesList.sort((a, b) => a?.name?.localeCompare(b?.name))
        semiValidCompaniesList.sort((a, b) => a?.name?.localeCompare(b?.name))
        units.sort((a, b) => a?.name.localeCompare(b?.name))
      }
      const finalValidCompaniesList = semiValidCompaniesList.concat(validCompaniesList)
      this.setState({
        validCompanies: finalValidCompaniesList,
        invalidCompanies: invalidCompaniesIds,
        validUnits: units,
        loading: false,
        showUnits: array,
      })
      this.validateApprovalFlowValidation(workflowForSteps)
    }
  }

  public render() {
    const { t } = this.props
    const {
      validationState,
      ownerIdToCheck,
      approvalProcessToSend,
      savingError,
      workflowForSteps,
      ownerOfID,
      loading,
      incompleteSteps,
    } = this.state
    const actualStepsOwners = workflowForSteps?.steps ?? []
    return (
      <Loader blocking={loading} style={{ overflow: 'hidden' }}>
        <div className="workflow row">
          <div className="ordersArea newFormArea col-5" style={{ minWidth: '0' }}>
            <div className="ordersFormArea">
              <div className="detailTitle">
                <i className="icon-edit" />
                <span>Edit Workflow</span>
              </div>
            </div>
            <div className="request-form-edit">
              <div className="formGroup">
                <label>Owner ID</label>
                <input
                  type="text"
                  name={`fieldnameveryunique${new Date().getTime()}`}
                  autoComplete="none"
                  list="autocompleteOff"
                  value={ownerIdToCheck}
                  onChange={(e) => this.handleInputChange(e)}
                  placeholder="do it"
                />
              </div>
              <label>Enter new approval process json</label>
              <textarea onChange={(e) => this.handleRawJSONChange(e.target.value)} value={approvalProcessToSend} />
              <div className="apply-btn">
                {this.state.jsonChanged && <button onClick={() => this.applyProcessJSONChange()}>Apply JSON</button>}
              </div>
            </div>
            <div className="btnArea">
              <NavLink to="/RequestsManagement/Workflows" className="btn btnClose">
                <i className="icon-close" /> CANCEL
              </NavLink>
              <a onClick={(e) => this.submit(e, true)} className="btn btnSave">
                <i className="icon-check" /> SUBMIT
              </a>
            </div>
            <Alert
              onClose={() => {
                this.closeSavingErrorAlert()
              }}
              visible={savingError.visible}
              title={savingError.title}
              message={savingError.message}
            />
            <Confirm
              visible={this.state.confirmingSave}
              title={'Confirmation'}
              message="One or more steps in the approval process are invalid. Do you want to continue saving?"
              onClose={(confirm) => {
                this.setState({ confirmingSave: false })
                if (confirm) {
                  this.submit(undefined, false)
                }
              }}
            />
          </div>
          <div className="workflowSteps col-4">
            <div className="detailTitle">
              <i className="icon-check" />
              <span>Approval Flow</span>
            </div>
            <div className="ordersContent">
              {this.state.approversValidating && (
                <div style={{ paddingBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                  {' '}
                  <LoadingSpinner />{' '}
                </div>
              )}
              {!!incompleteSteps.length && (
                <ul>
                  {incompleteSteps.map((s) => (
                    <li>
                      <span style={{ borderLeftColor: 'red' }}>
                        {`Incomplete step`}
                        <i className={'icon-warning'} />
                      </span>
                      <ul className="incomplete-step-issues">
                        <li>
                          <span className="ellipsis">{JSON.stringify(s)}</span>
                        </li>
                        {!Number.isInteger(s.stepNumber) && (
                          <li>
                            <i className={'icon-warning'} />
                            number property is missing or has invalid value
                          </li>
                        )}
                        {(!s.stepName || s.stepName.length < 3) && (
                          <li>
                            <i className={'icon-warning'} />
                            name property is missing or is shorter than 3 symbols
                          </li>
                        )}
                        <>
                          {!s.owners && (
                            <li>
                              <i className={'icon-warning'} />
                              owners array missing
                            </li>
                          )}
                          {!stepTypes.includes(s.stepType) && (
                            <li>
                              <i className={'icon-warning'} />
                              stepType property missing or has invalid value
                            </li>
                          )}
                        </>
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
              <ul>
                {validationState.map((step) => (
                  <li>
                    <span style={{ borderLeftColor: `${step.errorInStep ? 'red' : '#43B54A'}` }}>
                      STEP {step.step}
                      <i className={step.errorInStep ? 'icon-warning' : 'icon-check'} />
                    </span>
                    {Arr.any(Object.keys(step.users)) && (
                      <ul>
                        {Object.keys(step.users).map((key, i) => (
                          <li>
                            <ul>
                              {Arr.any(step.users[key]) ? (
                                step.users[key].map((u) => (
                                  <li
                                    className={`user ${
                                      PermissionManager.canApproveRequests(u) ? 'valid-approver' : 'invalid-approver'
                                    } ${u.enabled ? 'enabled' : 'disabled'}`}
                                  >
                                    <i className="icon-user" />
                                    {u.firstName} {u.lastName} <div className="email">{u.name}</div>
                                  </li>
                                ))
                              ) : (
                                <li className="notFound">
                                  <i className="icon-user" />{' '}
                                  {`Owner ${
                                    !!actualStepsOwners.length &&
                                    actualStepsOwners.find((ac) => ac.stepNumber === step.step)?.owners?.includes(key)
                                      ? key
                                      : actualStepsOwners.find((ac) => ac.stepNumber === step.step)?.owners?.[i]
                                  } not found`}
                                </li>
                              )}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {(workflowForSteps?.onRequestApprovedNotifiication?.cc.length ||
              workflowForSteps?.onRequestApprovedNotifiication?.bcc.length ||
              workflowForSteps?.requestApprovedNotifiicationEmails?.length) && (
              <div className="appNot">
                <div className="detailTitle">
                  <i className="icon-alert" />
                  <span>Approved notification</span>
                </div>
                <div className="ordersContent">
                  <ul>
                    {Arr.any(workflowForSteps?.requestApprovedNotifiicationEmails) && (
                      <li>
                        <ul>
                          {workflowForSteps?.requestApprovedNotifiicationEmails?.map((el) => (
                            <li>
                              <i className="icon-mail" /> {el}{' '}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                    {Arr.any(workflowForSteps?.onRequestApprovedNotifiication?.cc) && (
                      <li>
                        <span>cc:</span>
                        <ul>
                          {workflowForSteps?.onRequestApprovedNotifiication?.cc.map((el) => (
                            <li>
                              {' '}
                              <i className="icon-mail" /> {el}{' '}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                    {Arr.any(workflowForSteps?.onRequestApprovedNotifiication?.bcc) && (
                      <li>
                        <span>bcc:</span>
                        <ul>
                          {workflowForSteps?.onRequestApprovedNotifiication?.bcc.map((el) => (
                            <li>
                              {' '}
                              <i className="icon-mail" /> {el}{' '}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className="workflowSteps col-3">
            <div className="detailTitle">
              <i className="icon-groups" />
              <span>Owners</span>
            </div>
            <div className="ordersContent">
              <div className="owners">
                <i className="icon-groups" />
                {ownerOfID?.name && <div>{ownerOfID?.name}</div>}
              </div>
            </div>

            {Arr.any(this.state.invalidCompanies) && (
              <div className="valComp">
                <div className="detailTitle">
                  <span>
                    <i className="icon-units" />
                    <span>Invalid companies\units IDs</span>
                  </span>
                  <i className="icon-warning" style={{ color: 'red' }} />
                </div>
                <div className="ordersContent">
                  {this.state.invalidCompanies?.map((el) => (
                    <div>
                      <div className="compName invalidCompId">{el}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Arr.any(this.state.validCompanies) && (
              <div className="valComp">
                <div className="detailTitle">
                  <span>
                    <i className="icon-units" />
                    <span>Valid companies\units</span>
                  </span>
                  {this.state.validCompanies?.filter(
                    (co) => co?.tenantId?.toLowerCase() !== ownerIdToCheck?.toLowerCase()
                  ).length > 0 &&
                    ownerOfID.type === 'TENANT' && <i className="icon-warning" style={{ color: 'red' }} />}
                </div>
                <div className="ordersContent">
                  {this.state.validCompanies?.map((el) => (
                    <div>
                      <div
                        style={
                          el?.tenantId?.toLowerCase() !== ownerIdToCheck?.toLowerCase() && ownerOfID.type === 'TENANT'
                            ? { backgroundColor: 'red' }
                            : undefined
                        }
                        className="compName"
                      >
                        <div>
                          <i className="icon-customers" />
                          {el.name}
                        </div>
                        <div>
                          <i className="icon-id" /> <span>{el.reference}</span>
                          <i
                            className={`${
                              this.state.showUnits.filter((u) => u.id === el.id)[0]?.show ? 'icon-up' : 'icon-bottom'
                            }`}
                            onClick={() => this.switchShowUnits(el.id)}
                          />
                        </div>
                      </div>
                      {this.state.showUnits.filter((co) => co.id === el.id)[0]?.show && (
                        <ul className="unitsList">
                          {el.units
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((un) => (
                              <li>{un.name || t('NO_COST_CENTER')}</li>
                            ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {this.state.validUnits.map((el) => (
                    <div>
                      <ul className="unitsList">
                        <li>{el.name || t('NO_COST_CENTER')}</li>
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Loader>
    )
  }

  private switchShowUnits(id: string) {
    this.setState({
      showUnits: this.state.showUnits.map((u) => ({ ...u, show: u.id === id ? !u.show : u.show })),
    })
  }

  private defineWorkflow() {
    if (this.props.match.params.id === newProcessId) {
      const emptyApprovalProcess: ApprovalProcessConfigViewModel = {
        id: 'yes',
        name: 'yes',
        owner: 'yes',
        totalSteps: 0,
        tr: {},
        validCompanies: [],
      }
      this.setState({
        loading: false,
        currentWorkflow: emptyApprovalProcess,
        approvalProcessToSend: JSON.stringify(emptyApprovalProcess, null, '\t'),
      })
    } else {
      const match = this.state.workflowsConfigs.find((el) => el.id === this.props.match.params.id)
      if (match) {
        this.setState({
          workflowForSteps: match,
          currentWorkflow: match,
        })
      }
    }
  }

  private handleRawJSONChange(approvalProcessToSend: string) {
    this.setState({ approvalProcessToSend, jsonChanged: true })
  }

  private closeSavingErrorAlert() {
    const savingError: IAlertState = {
      ...this.state.savingError,
      visible: !this.state.savingError.visible,
    }
    this.setState({
      savingError,
    })
  }

  private handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      ownerIdToCheck: e.target.value,
    })
  }

  private validateApprovalFlowValidation(currentApprovalProcess: ApprovalProcessConfigViewModel) {
    const incompleteSteps: ApprovalProcessStepConfigViewModel[] = []
    const stepValidationData: ApproverValidationInputModel[] = []
    currentApprovalProcess?.steps?.forEach((s) => {
      if (
        Number.isInteger(s.stepNumber) &&
        s.owners &&
        s.stepName &&
        !s.owners.some((ow) => ow === '') &&
        s.stepName.length >= 3 &&
        stepTypes.includes(s.stepType)
      ) {
        stepValidationData.push({
          step: s.stepNumber,
          owners: s.owners,
          type:
            s.stepType === ApprovalProcessStepKind.BusinessUnit
              ? ApprovalStepTypeEnum.BU
              : s.stepType === ApprovalProcessStepKind.Company
              ? ApprovalStepTypeEnum.COM
              : ApprovalStepTypeEnum.DEFAULT,
        })
      } else {
        incompleteSteps.push(s)
      }
    })
    if (stepValidationData.length) {
      this.setState({
        approversValidating: true,
      })
      this.props.validateApprovalFlowUsers(currentApprovalProcess?.owner, stepValidationData).then(
        (validationResult) => {
          const validationState: StepWithError[] = validationResult.map((s) => ({ ...s }))
          validationState.forEach((el) => {
            el.errorInStep = false
            Object.values(el.users).forEach((users) =>
              users.length && el.errorInStep === false
                ? users.forEach((u) =>
                    PermissionManager.canApproveRequests(u)
                      ? !u.enabled
                        ? (el.errorInStep = true)
                        : (el.errorInStep = false)
                      : (el.errorInStep = true)
                  )
                : (el.errorInStep = true)
            )
          })
          this.setState({
            validationState,
            approversValidating: false,
          })
        },
        () => {
          this.setState({
            loading: false,
            approversValidating: false,
            savingError: {
              visible: true,
              message: this.props.t('ERROR_ON_OWNER_VALIDATION'),
              title: this.props.t('ERROR'),
            },
          })
        }
      )
    } else {
      this.resetStepsValidationState()
    }
    this.setState({
      incompleteSteps,
    })
  }

  private resetStepsValidationState() {
    this.setState({
      validationState: [],
    })
  }

  private applyProcessJSONChange(): ApprovalProcessConfigViewModel | undefined {
    try {
      const currentApprovalProcess = JSON.parse(this.state.approvalProcessToSend) as ApprovalProcessConfigViewModel

      if (currentApprovalProcess.owner) {
        this.setState({
          ownerIdToCheck: currentApprovalProcess.owner,
        })
      }

      this.setState({
        currentApprovalProcess,
        workflowForSteps: currentApprovalProcess,
        jsonChanged: false,
        isTagBased: Array.isArray(currentApprovalProcess?.stepsWithTags),
      })

      return currentApprovalProcess
    } catch {
      alert('Error parsing approval process JSON')
      return undefined
    }
  }

  private navigateToGrid(e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e?.preventDefault()
    const { history } = this.props
    history.push('/RequestsManagement/Workflows')
  }

  private submit(e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined, confirmValidation: boolean) {
    e?.preventDefault()
    const currentProcess = this.applyProcessJSONChange()

    if (currentProcess) {
      this.setState({
        loading: true,
      })

      const model: ApprovalProcessConfigInputModel = {
        ...currentProcess,
      }
      const stepError = this.state.validationState.some((v) => v.errorInStep)
      if (confirmValidation && stepError) {
        this.setState({ confirmingSave: true })
        return
      }

      const force = !confirmValidation && stepError

      const promise =
        this.props.match.params.id === newProcessId
          ? this.props.postNewRequestApprovalProcess(model, force)
          : this.props.putNewRequestApprovalProcess(this.props.match.params.id, model, force)

      promise.then(
        (savedProcess) => {
          alert(`Approval process saved! Process ID ${savedProcess.id}`)
          this.props.getWorkflows()
          this.navigateToGrid()
          this.setState({
            loading: false,
          })
        },
        () => {
          this.setState({
            loading: false,
            savingError: {
              visible: true,
              message: this.props.t('ERROR_ON_SAVING_FORM'),
              title: this.props.t('ERROR'),
            },
          })
        }
      )
    }
  }
}

/**
 * decide what part of state should be exposed
 * @param state  redux state
 * @param ownProps props of component
 */
function mapStateToProps({ customers }: State) {
  return {
    customers: customers.customers,
  }
}

/**
 * What action should be passed to component
 */
const mapDispatchToProps = {
  validateApprovalFlowUsers: wrapQuery((tenantId: string, steps: ApproverValidationInputModel[]) =>
    customersApi.endpoints.validateApprovers.initiate({ tenantId, steps })
  ),
  postNewRequestApprovalProcess: wrapQuery((model: ApprovalProcessConfigInputModel, force?: boolean) =>
    ordersApi.endpoints.postApprovalProcessConfig.initiate({ model, force })
  ),
  putNewRequestApprovalProcess: wrapQuery((id: string, model: ApprovalProcessConfigInputModel, force?: boolean) =>
    ordersApi.endpoints.putApprovalProcessConfig.initiate({ id, model, force })
  ),
  getWorkflows: wrapQuery(() => ordersApi.endpoints.getApprovalProcessConfigs.initiate()),
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
}

const connector = connect(mapStateToProps, mapDispatchToProps)
// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(RequestsAdminPanel))
